// Utility functions for image processing

const HEIC_IMAGE_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);

const HEIC_FILE_EXTENSION_PATTERN = /\.(heic|heif)$/i;

export const isHeicImageFile = (file: File): boolean =>
  HEIC_IMAGE_TYPES.has(file.type.toLowerCase()) ||
  HEIC_FILE_EXTENSION_PATTERN.test(file.name);

type Heic2AnyConverter = (options: {
  blob: Blob;
  toType: string;
}) => Promise<Blob | Blob[]>;

const loadHeic2Any = async (): Promise<Heic2AnyConverter> => {
  // Keep the browser-only package out of native startup while still making
  // Metro include it in the main web bundle. A dynamic import creates a lazy
  // module URL that is not reliably served by exported web builds.
  let candidate: unknown = require("heic2any");

  // Depending on the web bundler, the CommonJS package can be exposed as the
  // module itself, `default`, or `default.default`.
  for (
    let depth = 0;
    depth < 3 && typeof candidate !== "function";
    depth += 1
  ) {
    if (
      !candidate ||
      (typeof candidate !== "object" && typeof candidate !== "function") ||
      !("default" in candidate)
    ) {
      break;
    }
    candidate = candidate.default;
  }

  if (typeof candidate !== "function") {
    throw new Error("HEIC converter failed to load");
  }

  return candidate as Heic2AnyConverter;
};

export const createPngFileFromHeicConversion = (
  sourceFile: File,
  converted: Blob | Blob[],
): File => {
  const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
  if (!(convertedBlob instanceof Blob) || convertedBlob.size === 0) {
    throw new Error("HEIC converter returned no image data");
  }

  const baseName = sourceFile.name.includes(".")
    ? sourceFile.name.replace(/\.[^.]+$/, "")
    : sourceFile.name || "image";

  return new File([convertedBlob], `${baseName}.png`, {
    type: "image/png",
    lastModified: sourceFile.lastModified,
  });
};

export const convertHeicToPngFile = async (file: File): Promise<File> => {
  const heic2any = await loadHeic2Any();
  const converted = await heic2any({
    blob: file,
    toType: "image/png",
  });

  return createPngFileFromHeicConversion(file, converted);
};

export const resizeImage = (
  dataUrl: string,
  maxHeight: number,
): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = maxHeight / img.height;
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = maxHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = dataUrl;
  });
};

export const resizeImageToBlob = async (
  file: File,
  maxHeight: number = 1000,
  scaleUpIfSmaller: boolean = true,
): Promise<File> => {
  const dataUrl = URL.createObjectURL(file);

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = (err) => reject(err);
      image.src = dataUrl;
    });

    // Check if resizing is needed
    if (!scaleUpIfSmaller && img.height <= maxHeight) {
      // Just return original file
      return file;
    }

    const scale = maxHeight / img.height;
    const canvas = document.createElement("canvas");
    canvas.width = img.width * scale;
    canvas.height = maxHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas context is not available");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return new Promise<File>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Failed to create resized blob"));
        const resizedFile = new File([blob], file.name, { type: file.type });
        resolve(resizedFile);
      }, file.type);
    });
  } finally {
    URL.revokeObjectURL(dataUrl);
  }
};

export const prepareImageForUpload = async (
  file: File,
  maxHeight: number = 1000,
  scaleUpIfSmaller: boolean = true,
): Promise<File> => {
  const normalizedFile = isHeicImageFile(file)
    ? await convertHeicToPngFile(file)
    : file;

  return resizeImageToBlob(normalizedFile, maxHeight, scaleUpIfSmaller);
};

export const cropImage = async (
  url: string,
  cropX: number,
  cropY: number,
  cropWidth: number,
  cropHeight: number,
  rotationDegrees: number = 0,
): Promise<string> => {
  const normalizedRotation = ((rotationDegrees % 360) + 360) % 360;

  console.log("🟢 Cropping Image", {
    url,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    rotationDegrees: normalizedRotation,
  });

  // Bypass the HTML image cache: a previous non-CORS <img> load (e.g. RN Web's
  // <Image> for unpaired thumbnails) can poison the cache with an opaque entry
  // that fails subsequent CORS reads. `cache: "reload"` forces a fresh CORS
  // request so S3 returns proper Access-Control-Allow-Origin headers.
  const res = await fetch(url, { mode: "cors", cache: "reload" });
  if (!res.ok) {
    throw new Error(`cropImage fetch failed: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);

  try {
    const cropCanvas = document.createElement("canvas");
    const cropCtx = cropCanvas.getContext("2d");
    if (!cropCtx) throw new Error("No 2d context");

    cropCanvas.width = Math.max(1, Math.round(cropWidth));
    cropCanvas.height = Math.max(1, Math.round(cropHeight));

    cropCtx.drawImage(
      bitmap,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropCanvas.width,
      cropCanvas.height,
    );

    if (normalizedRotation === 0) {
      return cropCanvas.toDataURL("image/jpeg");
    }

    const isSideways = normalizedRotation === 90 || normalizedRotation === 270;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No 2d context");

    canvas.width = isSideways ? cropCanvas.height : cropCanvas.width;
    canvas.height = isSideways ? cropCanvas.width : cropCanvas.height;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((normalizedRotation * Math.PI) / 180);
    ctx.drawImage(cropCanvas, -cropCanvas.width / 2, -cropCanvas.height / 2);

    return canvas.toDataURL("image/jpeg");
  } finally {
    bitmap.close();
  }
};

export const ensurePngFile = async (file: File): Promise<File> => {
  if (file.type === "image/png") return file;

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = (err) => reject(err);
      image.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas context is not available");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const pngBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Failed to convert image to PNG"));
        resolve(blob);
      }, "image/png");
    });

    const baseName = file.name.includes(".")
      ? file.name.replace(/\.[^.]+$/, "")
      : file.name || "image";
    const pngName = `${baseName}.png`;
    return new File([pngBlob], pngName, { type: "image/png" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
